import { useCallback, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ViewToken } from 'react-native';
import {
  Typography,
  Flex,
  RemoteImage,
  IconLive,
  List,
  Colors,
} from '@banhala/ui-kit';
import type { LiveProfileBadgeListProps, LiveProfileBadgeItem } from './types';
import type { AnyObject } from '../../../../types';
import { bridge, isNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';

const PADDING_HORIZONTAL = 12;
const GAP = 8;
const PROFILE_SIZE = 72;
const IMAGE_SIZE = 65;
const BORDER_WIDTH = 2;
const BADGE_HEIGHT = 16;
const ITEM_WIDTH = PROFILE_SIZE;

type BadgeEntity = { item: LiveProfileBadgeItem; logging: AnyObject };

interface LiveProfileBadgeIconProps {
  item: LiveProfileBadgeItem;
  logging: AnyObject;
}

const LiveProfileBadgeIcon = ({ item, logging }: LiveProfileBadgeIconProps) => {
  const { name, profile_image_url, live_status, deeplink } = item;
  const isLive = live_status === 'LIVE';
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_LIVE_PROFILE_BADGE',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, deeplink, logging]);

  return (
    <Flex width={ITEM_WIDTH} alignItems="center" onPress={handlePress}>
      <View
        style={[
          styles.profileContainer,
          { borderColor: isLive ? '#EC0000' : '#DDDDDD' },
        ]}
      >
        <Flex
          width={IMAGE_SIZE}
          height={IMAGE_SIZE}
          borderRadius={IMAGE_SIZE / 2}
          overflow="hidden"
        >
          <RemoteImage
            uri={profile_image_url}
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
          />
        </Flex>
      </View>

      {isLive && (
        <View style={styles.liveBadge}>
          <IconLive width={17} height={8} fill={Colors.content_always_white} />
        </View>
      )}

      <Flex width="100%" margin={{ top: 8 }}>
        <Typography
          type="body4"
          color="content_primary"
          numberOfLines={1}
          textAlign="center"
        >
          {name}
        </Typography>
      </Flex>
    </Flex>
  );
};

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 1 };

const keyExtractor = (item: BadgeEntity) => item.item.sno.toString();

const renderItem = ({ item }: { item: BadgeEntity }) => (
  <LiveProfileBadgeIcon item={item.item} logging={item.logging} />
);

export const LiveProfileBadgeList = ({ entity }: LiveProfileBadgeListProps) => {
  const displayedRef = useRef(new Set<number>());

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        const badgeData = item as BadgeEntity;
        bridge.logAnalyticsEvent({
          name: 'DISPLAY_LIVE_PROFILE_BADGE',
          analytics: badgeData.logging.analytics,
          ably_only_log: badgeData.logging.ably_only_log,
        });
      });
    },
    [],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: PADDING_HORIZONTAL,
    gap: GAP,
    paddingBottom: 12,
  },
  profileContainer: {
    width: PROFILE_SIZE,
    height: PROFILE_SIZE,
    borderRadius: PROFILE_SIZE / 2,
    borderWidth: BORDER_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
  },
  liveBadge: {
    marginTop: -8,
    height: BADGE_HEIGHT,
    borderRadius: BADGE_HEIGHT / 2,
    backgroundColor: '#EC0000',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
});
